import { useEffect, useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router-dom';
import type { SupabaseClient } from '@supabase/supabase-js';
import { useAuth } from '../../../../../contexts/AuthContext';

// Follow-up reminders as their own layer on the booking calendar (Gary, 3 Nov 2026). Never a booking: no time slot,
// no availability, not counted in the booking totals. Each sits on its day as a violet chip; tap it to open the
// customer (or the lead). The same list Chats and the customer page show; the database decides who sees each one.

export interface CalendarReminder {
  id: string;
  date: string;
  name: string;
  note: string | null;
  territoryId: string | null;
  customerId: string | null;
  leadId: string | null;
}

interface Row {
  id: string;
  reminder_date: string;
  note: string | null;
  reminder_type: string;
  territory_id: string | null;
  customer_id: string | null;
  lead_id: string | null;
  customers: { customer_name: string | null; first_name: string | null; last_name: string | null } | null;
  leads: { first_name: string | null; last_name: string | null; company_name: string | null } | null;
}

const joined = (...parts: (string | null | undefined)[]) => parts.filter(Boolean).join(' ').trim();

// Loaded once per visit and shared by the month, week and day views.
let list: CalendarReminder[] = [];
let started = false;
const listeners = new Set<() => void>();

async function load(supabase: SupabaseClient): Promise<void> {
  const { data, error } = await supabase.from('customer_reminders')
    .select('id, reminder_date, note, reminder_type, territory_id, customer_id, lead_id, customers(customer_name, first_name, last_name), leads(first_name, last_name, company_name)')
    .eq('status', 'active').order('reminder_date', { ascending: true }).limit(1000);
  if (error) { started = false; return; }
  list = ((data ?? []) as unknown as Row[]).map((r) => ({
    id: r.id, date: r.reminder_date, note: r.note, territoryId: r.territory_id, customerId: r.customer_id, leadId: r.lead_id,
    name: r.customers?.customer_name?.trim() || joined(r.customers?.first_name, r.customers?.last_name)
      || joined(r.leads?.first_name, r.leads?.last_name) || r.leads?.company_name || 'A customer',
  }));
  for (const l of listeners) l();
}

interface ReminderFilters {
  territory_id: string;
  context: 'all' | 'pos' | 'cmb';
  show_reminders?: boolean;
}

/** Reminders on the calendar, narrowed by the calendar's area filter; none under POS (they are CleanMyBed follow-ups). */
export function useCalendarReminders(filters: ReminderFilters): { on: boolean; forDate: (date: string) => CalendarReminder[] } {
  const { supabase } = useAuth();
  const all = useSyncExternalStore((l) => { listeners.add(l); return () => listeners.delete(l); }, () => list);
  useEffect(() => {
    if (started) return;
    started = true;
    void load(supabase);
  }, [supabase]);
  const on = filters.show_reminders !== false && filters.context !== 'pos';
  const shown = on ? all.filter((r) => filters.territory_id === 'all' || r.territoryId === filters.territory_id) : [];
  return { on, forDate: (date) => shown.filter((r) => r.date === date) };
}

/** The chips for one day. `compact` is the phone's month grid: a thin bar per day with reminders. */
export function ReminderChips({ reminders, compact = false, limit = 2 }: { reminders: CalendarReminder[]; compact?: boolean; limit?: number }) {
  const navigate = useNavigate();
  if (!reminders.length) return null;
  if (compact) return <div className="cal-reminder-bar" title={`${reminders.length} reminder${reminders.length > 1 ? 's' : ''}`} />;
  const open = (r: CalendarReminder) => navigate(r.customerId ? `/customers?view=${r.customerId}` : `/leads?id=${r.leadId}`);
  return (
    <>
      {reminders.slice(0, limit).map((r) => (
        <button key={r.id} type="button" className="cal-reminder" title={`Reminder: ${r.name}${r.note ? `. ${r.note}` : ''}`}
          onClick={(e) => { e.stopPropagation(); open(r); }}>
          🔔 {r.name}
        </button>
      ))}
      {reminders.length > limit && <div className="cal-reminder-more">+{reminders.length - limit} reminder{reminders.length - limit > 1 ? 's' : ''}</div>}
    </>
  );
}
